import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CUSTOMER_EMAIL_WAIT_LINE,
  reminderStepLabel,
  type CustomerEmailWait,
  type PlannedLateFee,
} from '@servwell/shared';
import { todayISO } from '../../lib/format';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { SectionCard } from '../ui/Detail';
import { Notice, StatusBadge, type Tone } from '../ui/Badge';
import { Button } from '../ui/Button';
import { money } from '../money';

export interface LateFeeRow {
  id: string;
  sequence: number;
  amountCents: number;
  basisBalanceCents: number;
  kind: string;
  appliedOn: string;
  waivedAt: string | Date | null;
  waiveReason: string | null;
}
export interface ReminderRow {
  id: string;
  offsetDays: number;
  toEmail: string | null;
  status: string;
  detail: string | null;
  sentAt: string | Date;
  /** A `queued` step's email is waiting (MAP 249): why. Null when it is due, or final. */
  waitingFor?: string | null;
}

const shortDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString();

/**
 * The warning half of automatic late fees (PRD v2 §2.2). An automatic charge that a shop
 * only finds out about from an angry phone call costs more than it collects, so the next
 * fee is stated on the invoice before it exists — with the same arithmetic that will charge
 * it, because both come from `plannedLateFee`.
 */
export function NextLateFeeNotice({
  nextFee,
  tz,
}: {
  nextFee: PlannedLateFee | null;
  tz: string;
}) {
  if (!nextFee) return null;
  // Was the UTC date, so for several hours each evening this notice flipped to "now due"
  // a day before the fee is actually charged — the shop reading a threat it had not made.
  const today = todayISO(tz);
  const soon = nextFee.dueOn > today;
  return (
    <Notice tone={soon ? 'warn' : 'danger'}>
      {soon ? (
        <>
          A late fee of <strong>{money(nextFee.amountCents)}</strong> gets added on{' '}
          <strong>{shortDate(nextFee.dueOn)}</strong> unless this is paid.
        </>
      ) : (
        <>
          A late fee of <strong>{money(nextFee.amountCents)}</strong> is due to be added
          on tonight's run.
        </>
      )}
    </Notice>
  );
}

const REMINDER_TONE: Record<string, Tone> = {
  sent: 'success',
  skipped: 'neutral',
  failed: 'danger',
  // Owed, and in the outbox (MAP 249): the drain sends it and writes what became of it here.
  queued: 'info',
};

/** A reminder row's badge: what became of its email, never more than is known. */
export function reminderBadge(status: string): string {
  return status === 'sent'
    ? 'Sent'
    : status === 'queued'
      ? 'On its way'
      : status === 'failed'
        ? 'Not confirmed'
        : 'Not sent';
}

/** The reminder row's trailing words: why its email waits, or what happened (`detail`). */
export function reminderNote(r: Pick<ReminderRow, 'status' | 'detail' | 'waitingFor'>) {
  // A wait this build does not know (a newer server) says nothing rather than guess.
  if (
    r.status === 'queued' &&
    r.waitingFor &&
    Object.hasOwn(CUSTOMER_EMAIL_WAIT_LINE, r.waitingFor)
  )
    return CUSTOMER_EMAIL_WAIT_LINE[r.waitingFor as CustomerEmailWait];
  return r.detail;
}

/**
 * What has actually been done to chase this invoice. Kept off the page entirely when
 * nothing has — an empty "Collections" card on every invoice teaches people to skip it.
 */
export function InvoiceCollectionsCard({
  fees,
  reminders,
}: {
  fees: LateFeeRow[];
  reminders: ReminderRow[];
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [waiving, setWaiving] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Forgiving a fee the customer has already PAID leaves them owed the difference. The
  // server works it out (see waiveLateFee); this is the only place anyone would find out.
  const [overpaid, setOverpaid] = useState(0);

  const waive = useMutation(
    trpc.collections.waiveLateFee.mutationOptions({
      onSuccess: (result) => {
        setWaiving(null);
        setReason('');
        setError(null);
        setOverpaid(result.overpaidCents);
        void qc.invalidateQueries();
      },
      onError: (e) => setError(e.message),
    }),
  );

  if (fees.length === 0 && reminders.length === 0) return null;

  return (
    <SectionCard title="Chasing this one">
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {overpaid > 0 ? (
        <Notice tone="warn">
          They had already paid that fee, so they're {money(overpaid)} up. Give it back or
          knock it off their next invoice — nothing here does it for you.
        </Notice>
      ) : null}

      {fees.length > 0 ? (
        <ul className="divide-y divide-border text-sm">
          {fees.map((f) => {
            const waived = Boolean(f.waivedAt);
            return (
              <li key={f.id} className="py-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span
                    className={`font-medium tabular-nums ${waived ? 'text-muted-foreground line-through' : ''}`}
                  >
                    {money(f.amountCents)}
                  </span>
                  <StatusBadge tone={waived ? 'neutral' : 'danger'}>
                    {waived ? 'Waived' : `Late fee ${f.sequence}`}
                  </StatusBadge>
                  <span className="text-xs text-muted-foreground">
                    {shortDate(f.appliedOn)}
                    {f.kind === 'percent'
                      ? ` · on a ${money(f.basisBalanceCents)} balance`
                      : ''}
                  </span>
                  <span className="flex-1" />
                  {!waived ? (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setWaiving(waiving === f.id ? null : f.id);
                        setReason('');
                      }}
                    >
                      Take it off
                    </Button>
                  ) : null}
                </div>
                {f.waiveReason ? (
                  <p className="text-xs text-muted-foreground">“{f.waiveReason}”</p>
                ) : null}
                {waiving === f.id ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <input
                      className="min-w-0 flex-1 rounded-[var(--radius-sm)] border border-border bg-surface px-2.5 py-1.5 text-sm"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Why are you taking it off? (optional)"
                      aria-label="Reason for waiving the fee"
                      autoFocus
                    />
                    <Button
                      disabled={waive.isPending}
                      onClick={() =>
                        waive.mutate({
                          lateFeeId: f.id,
                          reason: reason.trim() || undefined,
                          ...idem(),
                        })
                      }
                    >
                      {waive.isPending ? 'Removing…' : 'Take it off'}
                    </Button>
                    <Button variant="ghost" onClick={() => setWaiving(null)}>
                      Cancel
                    </Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      {reminders.length > 0 ? (
        <div className={fees.length > 0 ? 'mt-3 border-t border-border pt-3' : ''}>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Reminders
          </p>
          <ul className="space-y-1 text-sm">
            {reminders.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2">
                {/* 'failed' has THREE flavours and one label, so the label has to be true
                    of all three: the sweep died between claiming this row and confirming
                    it; the mail service refused it; or the mail service never answered and
                    may well have accepted it. `detail`, on the same line, says which.

                    ⚠️ I changed this to "Didn't send" earlier in this ticket, reasoning that
                    "Not confirmed" was only true of the crash. Then the adversary pointed
                    out that `unreachable` is genuinely unknown — at which point "Didn't
                    send" is the sentence that over-claims, and the original label was right
                    for a reason I had not worked out yet. Reverted, with the reason. */}
                <StatusBadge tone={REMINDER_TONE[r.status] ?? 'neutral'}>
                  {reminderBadge(r.status)}
                </StatusBadge>
                <span>{reminderStepLabel(r.offsetDays)}</span>
                <span className="text-xs text-muted-foreground">
                  {new Date(r.sentAt).toLocaleDateString()}
                  {r.toEmail ? ` · ${r.toEmail}` : ''}
                  {reminderNote(r) ? ` · ${reminderNote(r)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </SectionCard>
  );
}
